import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
  input,
  output,
  viewChild,
  type ComponentRef,
} from '@angular/core';
import type { FocusOrigin } from '@angular/cdk/a11y';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { SUPPORT_CHANNEL_MENU, supportChannels, type SupportChannel } from './support-channels';

interface Anchor {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Invisible box over the clicked entry: the helm menu needs a trigger element to hang from. */
@Component({
  selector: 'onboarding-support-channel-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmDropdownMenuImports, IconComponent],
  host: {
    class: 'pointer-events-none fixed',
    '[style.left.px]': 'anchor().left',
    '[style.top.px]': 'anchor().top',
    '[style.width.px]': 'anchor().width',
    '[style.height.px]': 'anchor().height',
  },
  template: `
    <span
      class="block size-full"
      aria-hidden="true"
      [hlmDropdownMenuTrigger]="menu"
      (hlmDropdownMenuClosed)="closed.emit()"
    ></span>
    <ng-template #menu>
      <div hlmDropdownMenu [class]="look.plate">
        @for (channel of channels(); track channel.kind) {
          <button hlmDropdownMenuItem type="button" [class]="look.row" (triggered)="channel.open()">
            <app-icon [name]="channel.icon" [size]="16" />
            <span>{{ channel.label }}</span>
          </button>
          @if (channel.separatorAfter) {
            <hlm-dropdown-menu-separator />
          }
        }
      </div>
    </ng-template>
  `,
})
export class SupportChannelMenuComponent {
  readonly anchor = input.required<Anchor>();
  readonly channels = input.required<readonly SupportChannel[]>();
  readonly closed = output<void>();

  protected readonly look = SUPPORT_CHANNEL_MENU;
  private readonly trigger = viewChild.required(CdkMenuTrigger);

  open(origin: FocusOrigin): void {
    this.trigger().open();
    // Focus inside the menu, or Esc never reaches it.
    this.trigger().getMenu()?.focusFirstItem(origin);
  }
}

/** A support link keeps its `href`: a modified or non-left click stays the browser's, only a plain one opens the menu. */
export function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * The one channel choice for a support entry that is not a menu row (link, button, toast action):
 * opens the `supportChannels()` list at the element the photographer just pressed.
 */
@Injectable({ providedIn: 'root' })
export class SupportChannelMenuService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);
  readonly channels = supportChannels();

  private lastTarget: Element | null = null;
  private lastPoint = { x: 0, y: 0 };
  private lastInputKeyboard = false;
  private ref: ComponentRef<SupportChannelMenuComponent> | null = null;

  constructor() {
    // Capture: a handler that stops propagation still leaves its press here.
    this.document.addEventListener(
      'pointerdown',
      (event: MouseEvent) => {
        this.lastTarget = event.target instanceof Element ? event.target : null;
        this.lastPoint = { x: event.clientX, y: event.clientY };
        this.lastInputKeyboard = false;
      },
      { capture: true, passive: true },
    );
    // A key pressed after the last press means the next open comes from the keyboard: the press no longer anchors it.
    this.document.addEventListener(
      'keydown',
      () => {
        this.lastTarget = null;
        this.lastInputKeyboard = true;
      },
      { capture: true, passive: true },
    );
  }

  /** True from `open` until the menu's close lands, whatever closed it. */
  get isOpen(): boolean {
    return this.ref !== null;
  }

  /** `channels` lets a host swap one row's opener; every other caller takes the shared list. */
  open(channels: readonly SupportChannel[] = this.channels()): void {
    for (const channel of channels) channel.prepare?.();
    this.ref?.destroy();
    const opener = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
    const ref = createComponent(SupportChannelMenuComponent, { environmentInjector: this.injector });
    ref.setInput('anchor', this.anchor());
    // One press anchors one open: a later keyboard activation must not inherit it.
    this.lastTarget = null;
    ref.setInput('channels', channels);
    this.appRef.attachView(ref.hostView);
    this.document.body.appendChild(ref.location.nativeElement);
    ref.changeDetectorRef.detectChanges();
    // CDK detaches the overlay right after `closed` fires: destroy once it is done.
    ref.instance.closed.subscribe(() =>
      queueMicrotask(() => {
        if (this.ref === ref) this.ref = null;
        ref.destroy();
        // The trigger is an invisible span: without this, focus falls to body — and out of a dialog that opened the menu.
        const lost = !this.document.activeElement || this.document.activeElement === this.document.body;
        if (lost && opener?.isConnected) opener.focus();
      }),
    );
    this.ref = ref;
    ref.instance.open(this.lastInputKeyboard ? 'keyboard' : 'mouse');
  }

  private anchor(): Anchor {
    const trigger = 'a, button, [role="button"]';
    const el = this.lastTarget?.isConnected
      ? (this.lastTarget.closest(trigger) ?? this.lastTarget)
      : // Keyboard activation records no press: the focused control is the one activated.
        this.document.activeElement?.closest(trigger);
    if (el) {
      const { left, top, width, height } = el.getBoundingClientRect();
      return { left, top, width, height };
    }

    return { left: this.lastPoint.x, top: this.lastPoint.y, width: 0, height: 0 };
  }
}
